import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Dialog, Typography } from "@mui/material";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS } from "./constants";
import { resolveSlot } from "../../../utils/slotMapping";

interface ActivateGameModalProps {
  open: boolean;
  onClose: () => void;
  socket: any;
  apiBaseURL: string;
  shopId: any;
  onActivated: () => void;
}

const SCREENS = [1, 2]; // the product supports two screens per shop

// Activate popup (Figma node 2009:104). Marks a confirmed pack ACTIVE and places
// its game on a screen slot. Stays open after each activation so the user can
// activate multiple tickets back to back; only Close / x dismisses it.
const ActivateGameModal: React.FC<ActivateGameModalProps> = ({
  open,
  onClose,
  socket,
  apiBaseURL,
  shopId,
  onActivated,
}) => {
  //@ts-ignore
  const config = window.config;
  const defaultPerScreen = Number(config?.ticketsPerScreen ?? 0);
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  const [slotNumber, setSlotNumber] = React.useState<string>("");
  const [perScreenCounts, setPerScreenCounts] = React.useState<number[]>([]);
  const [activating, setActivating] = React.useState<boolean>(false);

  const clearFields = () => {
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
    setSlotNumber("");
  };

  // Load each screen's ticket count so a global slot number can be resolved to a
  // (screen, position). Missing screens fall back to the configured default.
  React.useEffect(() => {
    if (!open) return;
    Promise.all(
      SCREENS.map((screenNumber) =>
        axios
          .get(`${apiBaseURL}/api/settings/fetchSettings?id=${shopId}&screenNumber=${screenNumber}`)
          .then((res: any) => Number(res?.data?.settings?.ticketsCount ?? defaultPerScreen) || 0)
          .catch(() => 0),
      ),
    ).then(setPerScreenCounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiBaseURL, shopId]);

  // Socket acks are dedicated to this popup; broadcast placement goes to the
  // dashboard/TV via the existing "fetchTicketByNumberResponse" event.
  React.useEffect(() => {
    if (!socket) return;

    const onSuccess = () => {
      setActivating(false);
      showSuccessMessage(t("activateSuccessMsg", "Ticket activated successfully"));
      clearFields();
      onActivated();
    };
    const onError = (response: any) => {
      setActivating(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };

    socket.on("activateInventoryV2Response", onSuccess);
    socket.on("activateInventoryV2Error", onError);
    return () => {
      socket.off("activateInventoryV2Response", onSuccess);
      socket.off("activateInventoryV2Error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length >= 11) {
      setGameNumber(value.substring(0, 4));
      setPackNumber(value.substring(4, 11));
    }
  };

  const handleActivate = () => {
    if (!socket) {
      return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    }
    if (!gameNumber) {
      return showErrorMessage(t("gameNumberRequiredText", "Please enter a game number"));
    }
    if (!/^\d{7}$/.test(packNumber)) {
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    const slot = Number(slotNumber);
    if (!Number.isInteger(slot) || slot <= 0) {
      return showErrorMessage(t("slotNumberError", "Please enter a valid slot number"));
    }
    const resolved = resolveSlot(slot, perScreenCounts);
    if (!resolved) {
      return showErrorMessage(t("slotOutOfRange", "Slot number is out of the configured screen range"));
    }

    setActivating(true);
    socket.emit("activateInventoryV2", {
      shopId,
      gameNumber,
      packNumber,
      screenNumber: resolved.screenNumber,
      ticketPosition: resolved.ticketPosition,
    });
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const labelSx = { fontSize: "18px", color: "#434343", mb: "6px" } as const;
  const inputSx = {
    width: "100%",
    height: "40px",
    borderRadius: "10px",
    border: `1px solid ${COLORS.border}`,
    backgroundColor: "#fff",
    px: "12px",
    outline: "none",
    fontSize: "17px",
  } as const;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // popup only closes via the explicit Close / x controls
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      PaperProps={{
        sx: {
          width: "505px",
          maxWidth: "95vw",
          borderRadius: "10px",
          border: `2px solid ${COLORS.orange}`,
          backgroundColor: COLORS.panelBg,
          overflow: "hidden",
        },
      }}
    >
      {/* Orange header */}
      <Box
        sx={{ backgroundColor: COLORS.orange, height: "43px", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}
      >
        <Typography sx={{ color: "#fff", fontSize: "22px", fontWeight: 600, letterSpacing: "1px" }}>
          {t("activateGameTitle", "Activate Game")}
        </Typography>
        <Box
          component='span'
          onClick={handleClose}
          sx={{ color: "#fff", fontSize: "26px", cursor: "pointer", lineHeight: 1 }}
          aria-label={t("close", "Close")}
        >
          ×
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Box mb={1.5}>
          <Typography sx={labelSx}>{t("scanCodeLabel", "Scan Code")}</Typography>
          <Box component='input' value={scanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)} sx={inputSx} />
        </Box>
        {/* Orange divider separating scan entry from manual entry (Figma node 2009:114) */}
        <Box sx={{ height: "2px", backgroundColor: COLORS.orange, mx: -1.5, mb: 2.5 }} />
        <Box mb={2}>
          <Typography sx={labelSx}>{t("gameNumberLabel", "Game Number")}</Typography>
          <Box component='input' value={gameNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)} sx={inputSx} />
        </Box>
        <Box mb={2}>
          <Typography sx={labelSx}>{t("packNumberLabel", "Pack Number")}</Typography>
          <Box component='input' value={packNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)} sx={inputSx} />
        </Box>
        <Box mb={1}>
          <Typography sx={labelSx}>{t("slotNumberLabel", "Slot Number")}</Typography>
          <Box component='input' value={slotNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlotNumber(e.target.value)} sx={inputSx} />
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${COLORS.orange}`, display: "flex", justifyContent: "flex-end", gap: "12px", p: 2 }}>
        <Button
          onClick={handleClose}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "2px", px: 3, fontSize: "16px", fontWeight: 600, letterSpacing: "1px", "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("close", "Close")}
        </Button>
        <Button
          onClick={handleActivate}
          disabled={activating}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "2px", px: 3, fontSize: "16px", fontWeight: 600, letterSpacing: "1px", "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("activateButton", "Activate")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ActivateGameModal;
